import { useEffect, useState } from 'react';
import type { ConVersion } from '../../api/cliente';
import type { Campana, CanalCampana, ContenidoCampana, NuevaCampana, PlantillaMarketing, ProblemaCupo, VistaPrevia } from '../../api/ops/campanas';
import { ErrorApi } from '../../api/problema';
import { useAvisoBreve } from '../../componentes/avisoBreve';
import { Boton } from '../../componentes/Boton';
import { Campo } from '../../componentes/Campo';
import { CampoImporte } from '../../componentes/CampoImporte';
import { Desplegable } from '../../componentes/Desplegable';
import { Fecha } from '../../componentes/Fecha';
import { Icono } from '../../componentes/Icono';
import { Interruptor } from '../../componentes/Interruptor';
import { OpcionRadio } from '../../componentes/OpcionRadio';
import { PanelLateral } from '../../componentes/PanelLateral';
import { Segmentos } from '../../componentes/Segmentos';
import { Ventana } from '../../componentes/Ventana';
import { avisoBreveDeError } from '../../logica/estados';
import { cifra, diaYHora, hoyLocal } from '../../logica/fechasCortas';
import type { PropsApartado } from './Campanas';
import { CANAL, canalEnFrase, RUTA_PLAN, RUTA_PLANTILLAS_META } from './reglas';
import { borradorDe, contenidoDe, erroresEditor, HORAS, segmentoDe, variablesDe, type Borrador } from './editor';

/**
 * 12.2 Crear una campaña: RV Panel por pasos Canal · Mensaje · A quién · Envío. Al pasar a «A quién» se guarda el borrador
 * (`createCampaign` o `updateCampaign` con `If-Match`) y se calcula la vista previa (`previewCampaign`: `audience_size`,
 * `excluded_no_consent`, lo que gasta, `estimated_send_minutes` G707, `shortfall`, `sample` con «Publicidad» y
 * `unsubscribe_footer` tal cual, G735). Enviar (`sendCampaign`) o programar (`scheduleCampaign`) con la `preview_version`
 * tras una ventana con las cifras. Comprar lleva a Plan mientras no haya Stripe (G561). También edita una programada
 * (vuelve a programarla con la vista previa nueva).
 */
interface Props extends PropsApartado {
    campana: ConVersion<Campana> | null;
    onCerrar: (texto?: string) => void;
}

const PASOS = [
    { etiqueta: 'Canal', icono: 'send' },
    { etiqueta: 'Mensaje', icono: 'message-square-text' },
    { etiqueta: 'A quién', icono: 'users' },
    { etiqueta: 'Envío', icono: 'calendar-clock' },
] as const;

interface Opcion {
    id: string;
    nombre: string;
}

/** «12/11/2026» para distinguir dos eventos con el mismo nombre. */
const fechaEvento = (fecha: string): string => fecha.split('-').reverse().join('/');

export function Editor({ ops, negocio, movil, navegar, membresia, campana, onCerrar }: Props) {
    const aviso = useAvisoBreve();
    const [b, setB] = useState<Borrador>(() => borradorDe(campana?.datos ?? null));
    const [id, setId] = useState<string | null>(campana?.datos.id ?? null);
    const [version, setVersion] = useState<string | null>(campana?.version ?? null);
    const programada = campana?.datos.status === 'scheduled';
    const [paso, setPaso] = useState(0);
    const [plantillas, setPlantillas] = useState<PlantillaMarketing[]>([]);
    const [etiquetas, setEtiquetas] = useState<{ id: string; nombre: string }[]>([]);
    const [vinoA, setVinoA] = useState<{ servicios: Opcion[]; eventos: Opcion[] }>({ servicios: [], eventos: [] });
    const [vista, setVista] = useState<VistaPrevia | null>(null);
    const [sucio, setSucio] = useState(true);
    const [ocupado, setOcupado] = useState(false);
    const [intentado, setIntentado] = useState(false);
    const [confirmar, setConfirmar] = useState(false);
    const [borrar, setBorrar] = useState(false);

    useEffect(() => {
        let vivo = true;
        ops.listMarketingTemplates(negocio, { status: 'approved', limit: 100 })
            .then((r) => {
                if (vivo) {
                    setPlantillas(r.data);
                }
            })
            .catch(() => undefined);
        // «Vino a» (revisión 12.6): los servicios activos y los eventos publicados o ya pasados; sin permiso, sin el grupo.
        void Promise.all([
            ops.listServices(negocio, { status: 'active', limit: 100 }).catch(() => null),
            // Publicados y ya pasados en una sola llamada (G827).
            ops.listEvents(negocio, { status: ['published', 'ended'], limit: 100 }).catch(() => null),
            ops.getBusinessConVersion(negocio).catch(() => null),
        ]).then(([servicios, eventos, n]) => {
            if (!vivo) {
                return;
            }
            setVinoA({
                servicios: (servicios?.data ?? []).map((s) => ({ id: s.id, nombre: s.current_version.name })),
                eventos: (eventos?.data ?? []).map((e) => ({ id: e.id, nombre: `${e.name ?? ''} · ${fechaEvento(e.local_start_date)}` })),
            });
            const moneda = n?.datos.currency;
            if (moneda !== undefined) {
                setB((x) => (x.gasto === '' ? { ...x, moneda } : x));
            }
        });
        ops.listTags(negocio, { limit: 100 })
            .then((r) => {
                if (vivo) {
                    setEtiquetas(r.data.map((t) => ({ id: t.id, nombre: t.name })));
                }
            })
            .catch(() => undefined);
        return () => {
            vivo = false;
        };
    }, [ops, negocio]);

    const cambiar = (c: Partial<Borrador>) => {
        setB((x) => ({ ...x, ...c }));
        setSucio(true);
    };
    const errores = erroresEditor(b, paso);
    const plantilla = plantillas.find((p) => p.template_id === b.plantilla);

    const fallo = (e: unknown, reintentar: () => void) => {
        if (e instanceof ErrorApi && e.estado === 412) {
            aviso({ tipo: 'error', texto: 'Alguien ha cambiado la campaña: ciérrala y vuelve a abrirla.' });
            return;
        }
        aviso({ tipo: 'error', texto: avisoBreveDeError(e) ?? (e instanceof Error ? e.message : 'No se ha podido guardar.'), reintentar });
    };

    /** Guarda el borrador (crear o cambiar) y devuelve su id y su versión. */
    const guardar = async (): Promise<{ id: string; version: string | null }> => {
        const datos: NuevaCampana = { name: b.nombre.trim(), channel: b.canal, segment: segmentoDe(b), age_restricted: b.edad, ...(contenidoDe(b) === null ? {} : { content: contenidoDe(b) as ContenidoCampana }) };
        const r = id === null || version === null ? await ops.createCampaign(negocio, datos) : await ops.updateCampaign(negocio, id, datos, version);
        setId(r.datos.id);
        setVersion(r.version);
        return { id: r.datos.id, version: r.version };
    };
    /** Guarda y calcula la vista previa (12.3 de la revisión: al pasar a «A quién»). */
    const calcular = async (): Promise<VistaPrevia> => {
        const g = await guardar();
        const p = await ops.previewCampaign(negocio, g.id);
        // La vista previa cambia la versión de la campaña: se vuelve a leer su ETag.
        const leida = await ops.getCampaign(negocio, g.id);
        setVersion(leida.version);
        setVista(p);
        setSucio(false);
        return p;
    };
    const conOcupado = (f: () => Promise<unknown>, reintentar: () => void) => {
        setOcupado(true);
        f()
            .catch((e: unknown) => {
                fallo(e, reintentar);
            })
            .finally(() => {
                setOcupado(false);
            });
    };

    const siguiente = () => {
        setIntentado(true);
        if (Object.keys(errores).length > 0) {
            return;
        }
        setIntentado(false);
        if (paso === 1 || (paso === 2 && sucio)) {
            conOcupado(async () => {
                await calcular();
                setPaso(paso + 1);
            }, siguiente);
            return;
        }
        if (paso < 3) {
            setPaso(paso + 1);
            return;
        }
        if (sucio) {
            conOcupado(calcular, siguiente);
            return;
        }
        setConfirmar(true);
    };

    const enviar = () => {
        if (id === null || vista === null) {
            return;
        }
        conOcupado(async () => {
            try {
                if (b.cuando === 'ahora') {
                    await ops.sendCampaign(negocio, id, vista.preview_version);
                    onCerrar(`«${b.nombre.trim()}» se está enviando.`);
                } else {
                    await ops.scheduleCampaign(negocio, id, { preview_version: vista.preview_version, local_date: b.fecha, local_time: b.hora }, version ?? '');
                    onCerrar(`«${b.nombre.trim()}» programada para el ${b.fecha.split('-').reverse().join('/')} a las ${b.hora}.`);
                }
            } catch (e: unknown) {
                setConfirmar(false);
                if (e instanceof ErrorApi && e.codigo === 'conflict') {
                    const p = e.problema as ProblemaCupo;
                    aviso({ tipo: 'error', texto: p.blocking_reason === 'stale_preview' ? 'Ha cambiado algo: calculamos otra vez lo que gasta.' : (p.detail ?? p.title) });
                    await calcular();
                    return;
                }
                throw e;
            }
        }, enviar);
    };

    const ultimo = paso === 3;
    const textoAccion = !ultimo ? 'Siguiente' : sucio ? 'Calcular otra vez' : programada ? 'Guardar' : b.cuando === 'ahora' ? 'Enviar' : 'Programar';

    return (
        <>
            <PanelLateral
                abierto
                onCerrar={() => { onCerrar(id === null ? undefined : 'Borrador guardado.'); }}
                movil={movil}
                titulo={b.nombre.trim() === '' ? 'Nueva campaña' : b.nombre.trim()}
                eyebrow={programada ? 'Cambiar la campaña programada' : id === null ? 'Crear una campaña' : 'Borrador de campaña'}
                subtitulo={CANAL[b.canal].nombre}
                icono={CANAL[b.canal].icono}
                accion={textoAccion}
                accionIcono={!ultimo ? 'arrow-right' : b.cuando === 'ahora' && !programada ? 'send' : 'calendar-clock'}
                ocupado={ocupado}
                ocupadoTexto={paso === 1 || sucio ? 'Calculando…' : 'Guardando…'}
                onAccion={siguiente}
            >
                <div className="rv-cp-form">
                    <Segmentos etiqueta="Pasos de la campaña" movil={movil} items={PASOS} valor={paso} onCambio={(i) => { if (i < paso) { setPaso(i); } }} />
                    {paso === 0 && (
                        <>
                            <Campo etiqueta="Nombre de la campaña" valor={b.nombre} onChange={(v) => { cambiar({ nombre: v }); }} max={120} error={intentado ? (errores.nombre ?? null) : null} />
                            {(Object.keys(CANAL) as CanalCampana[]).map((k) => (
                                <OpcionRadio key={k} elegida={b.canal === k} onElegir={() => { cambiar({ canal: k }); }} icono={CANAL[k].icono} titulo={CANAL[k].nombre} texto={TEXTO_CANAL[k]} />
                            ))}
                            <p className="rv-ca-nota">
                                <Icono nombre="info" />
                                <span>Instagram todavía no se puede usar para campañas. El canal se elige aquí aunque «Enviar por WhatsApp» de Mensajes esté apagado.</span>
                            </p>
                        </>
                    )}
                    {paso === 1 && b.canal === 'whatsapp' && (
                        <>
                            <Desplegable
                                etiqueta="Plantilla"
                                valor={b.plantilla}
                                opciones={[{ valor: '', texto: plantillas.length === 0 ? 'No hay plantillas aprobadas' : 'Elige una plantilla' }, ...plantillas.map((p) => ({ valor: p.template_id, texto: p.name }))]}
                                onChange={(v) => { cambiar({ plantilla: v, variables: variablesDe(plantillas.find((p) => p.template_id === v)?.body ?? '') }); }}
                                error={intentado ? (errores.plantilla ?? null) : null}
                                ayuda="Por WhatsApp solo se puede escribir con plantillas que Meta haya aprobado."
                            />
                            <button type="button" className="rv-link" onClick={() => { navegar(RUTA_PLANTILLAS_META); }}>
                                Nueva plantilla
                            </button>
                            {plantilla?.body != null && <div className="rv-cp-muestra">{plantilla.body}</div>}
                            {Object.keys(b.variables).map((k) => (
                                <Desplegable
                                    key={k}
                                    etiqueta={`Variable {{${k}}}`}
                                    valor={b.variables[k] ?? 'customer_first_name'}
                                    opciones={[
                                        { valor: 'customer_first_name', texto: 'Nombre del cliente' },
                                        { valor: 'business_name', texto: 'Nombre del negocio' },
                                        { valor: 'booking_link', texto: 'Enlace para reservar' },
                                    ]}
                                    onChange={(v) => { cambiar({ variables: { ...b.variables, [k]: v as Borrador['variables'][string] } }); }}
                                />
                            ))}
                        </>
                    )}
                    {paso === 1 && b.canal === 'telegram' && <Campo etiqueta="Mensaje" valor={b.texto} onChange={(v) => { cambiar({ texto: v }); }} filas={6} max={4096} error={intentado ? (errores.texto ?? null) : null} />}
                    {paso === 1 && b.canal === 'email' && (
                        <>
                            <Campo etiqueta="Asunto" valor={b.asunto} onChange={(v) => { cambiar({ asunto: v }); }} error={intentado ? (errores.asunto ?? null) : null} />
                            <Campo etiqueta="Texto" valor={b.cuerpo} onChange={(v) => { cambiar({ cuerpo: v }); }} filas={8} error={intentado ? (errores.cuerpo ?? null) : null} ayuda="Puedes usar negrita, listas, enlaces e imágenes; lo demás se quita al enviar." />
                        </>
                    )}
                    {paso === 2 && (
                        <>
                            {etiquetas.length > 0 && (
                                // 12.2: las etiquetas como pastillas que se encienden; «Si eliges varias» solo con más de una elegida.
                                <div className="rv-cp-etiquetas">
                                    <span className="rv-cp-etiquetas__cab">
                                        <span>Etiquetas</span>
                                        <span>{b.etiquetas.length > 0 ? `${String(b.etiquetas.length)} elegida${b.etiquetas.length > 1 ? 's' : ''}. Sin ninguna, va a todos.` : 'Sin ninguna, va a todos los clientes con permiso.'}</span>
                                    </span>
                                    <div role="group" aria-label="Etiquetas" className="rv-cp-pastillas">
                                        {etiquetas.map((t) => {
                                            const on = b.etiquetas.includes(t.id);
                                            return (
                                                <button key={t.id} type="button" aria-pressed={on} className={`rv-cp-pastilla${on ? ' rv-cp-pastilla--on' : ''}`} onClick={() => { cambiar({ etiquetas: on ? b.etiquetas.filter((x) => x !== t.id) : [...b.etiquetas, t.id] }); }}>
                                                    <Icono nombre={on ? 'check' : 'tag'} />
                                                    <span>{t.nombre}</span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                    {b.etiquetas.length > 1 && (
                                        <div className="rv-cp-etiquetas__modo">
                                            <Desplegable etiqueta="Si eliges varias" valor={b.modoEtiquetas} opciones={[{ valor: 'any', texto: 'Con alguna' }, { valor: 'all', texto: 'Con todas' }]} onChange={(v) => { cambiar({ modoEtiquetas: v === 'all' ? 'all' : 'any' }); }} />
                                        </div>
                                    )}
                                </div>
                            )}
                            <div className="rv-cp-fila">
                                <Desplegable
                                    etiqueta="Última visita"
                                    valor={b.ultima}
                                    opciones={[
                                        { valor: '', texto: 'Cualquier fecha' },
                                        { valor: 'in30', texto: 'En los últimos 30 días' },
                                        { valor: 'gt60', texto: 'Hace más de 60 días' },
                                        { valor: 'gt90', texto: 'Hace más de 90 días' },
                                        { valor: 'gt180', texto: 'Hace más de 180 días' },
                                    ]}
                                    onChange={(v) => { cambiar({ ultima: v as Borrador['ultima'] }); }}
                                />
                                <Desplegable
                                    etiqueta="Visitas"
                                    valor={b.visitas}
                                    opciones={[
                                        { valor: '', texto: 'Cualquiera' },
                                        { valor: '1', texto: 'Al menos 1' },
                                        { valor: '2', texto: 'Al menos 2' },
                                        { valor: '5', texto: 'Al menos 5' },
                                    ]}
                                    onChange={(v) => { cambiar({ visitas: v }); }}
                                />
                                <CampoImporte etiqueta="Gasto mínimo" moneda={b.moneda} valor={b.gasto} onChange={(v) => { cambiar({ gasto: v }); }} ayuda="Lo que ha gastado en total." error={intentado ? (errores.gasto ?? null) : null} />
                                {/* «Vino a» (G940, G941): un desplegable por grupo que exista, evento y servicio, un valor en cada uno. */}
                                {vinoA.eventos.length > 0 && (
                                    <Desplegable etiqueta="Vino a (evento)" valor={b.eventos[0] ?? ''} opciones={[{ valor: '', texto: 'Cualquier evento' }, ...vinoA.eventos.map((o) => ({ valor: o.id, texto: o.nombre }))]} onChange={(v) => { cambiar({ eventos: v === '' ? [] : [v] }); }} />
                                )}
                                {vinoA.servicios.length > 0 && (
                                    <Desplegable etiqueta="Vino a (servicio)" valor={b.servicios[0] ?? ''} opciones={[{ valor: '', texto: 'Cualquier servicio' }, ...vinoA.servicios.map((o) => ({ valor: o.id, texto: o.nombre }))]} onChange={(v) => { cambiar({ servicios: v === '' ? [] : [v] }); }} />
                                )}
                            </div>
                            <Interruptor etiqueta="Campaña de alcohol u ocio nocturno" valor={b.edad} onChange={(v) => { cambiar({ edad: v }); }} ayuda="No la reciben los clientes que en su ficha tienen menos de 18 años." />
                            {vista !== null && (
                                <dl className="rv-cp-coste" aria-live="polite">
                                    <div>
                                        <dt>La recibirán unos</dt>
                                        <dd>{cifra(vista.audience_size)}</dd>
                                    </div>
                                    <div>
                                        <dt>Fuera por no tener permiso</dt>
                                        <dd>{cifra(vista.excluded_no_consent)}</dd>
                                    </div>
                                </dl>
                            )}
                            {sucio && vista !== null && <small>Has cambiado a quién va: al seguir se calcula otra vez.</small>}
                            <p className="rv-ca-nota">
                                <Icono nombre="shield-check" />
                                <span>{`Solo la reciben quienes aceptaron ofertas por ${canalEnFrase(b.canal)}. Los demás se cuentan, nunca se nombran.`}</span>
                            </p>
                        </>
                    )}
                    {paso === 3 && (
                        <>
                            <OpcionRadio elegida={b.cuando === 'ahora'} onElegir={() => { cambiar({ cuando: 'ahora' }); setSucio(false); }} icono="send" titulo="Ahora" />
                            <OpcionRadio elegida={b.cuando === 'luego'} onElegir={() => { cambiar({ cuando: 'luego' }); setSucio(false); }} icono="calendar-clock" titulo="Programar" texto="Hora del negocio." />
                            {b.cuando === 'luego' && (
                                <div className="rv-cp-fila">
                                    <Fecha etiqueta="Día" valor={b.fecha} min={hoyLocal()} onChange={(v) => { setB((x) => ({ ...x, fecha: v })); }} error={intentado ? (errores.fecha ?? null) : null} />
                                    <Desplegable etiqueta="Hora" valor={b.hora} opciones={HORAS.map((h) => ({ valor: h, texto: h }))} onChange={(v) => { setB((x) => ({ ...x, hora: v })); }} />
                                </div>
                            )}
                            {vista !== null && <LoQueGasta vista={vista} canal={b.canal} ahora={b.cuando === 'ahora'} />}
                            {vista !== null && !vista.enough && (
                                <div className="rv-cn-alerta rv-cn-alerta--amarilla" role="alert">
                                    <Icono nombre="triangle-alert" />
                                    <span>
                                        <strong>No llega para todos</strong>
                                        <span>
                                            {vista.shortfall?.sends_missing != null && vista.shortfall.sends_missing > 0 ? `Faltan ${cifra(vista.shortfall.sends_missing)} envíos. ` : ''}
                                            {vista.shortfall?.marketing_messages_missing != null && vista.shortfall.marketing_messages_missing > 0 ? `Faltan ${cifra(vista.shortfall.marketing_messages_missing)} mensajes del paquete de marketing. ` : ''}
                                            No sale a medias.
                                        </span>
                                        <span className="rv-cp-franja__botones">
                                            {(vista.shortfall?.options ?? []).map((o) =>
                                                o === 'buy_extra_sends' || o === 'buy_marketing_pack' ? (
                                                    membresia?.is_owner === true ? (
                                                        <Boton key={o} icono="shopping-cart" movil={movil} onClick={() => { navegar(RUTA_PLAN); }}>
                                                            {o === 'buy_extra_sends' ? 'Ampliar envíos' : 'Comprar paquete Marketing'}
                                                        </Boton>
                                                    ) : null
                                                ) : o === 'narrow_segment' ? (
                                                    <Boton key={o} tipo="secundario" icono="users" movil={movil} onClick={() => { setPaso(2); }}>
                                                        Afinar a quién
                                                    </Boton>
                                                ) : (
                                                    <Boton key={o} tipo="secundario" icono="mail" movil={movil} onClick={() => { cambiar({ canal: 'email' }); setPaso(1); }}>
                                                        Pasar a email
                                                    </Boton>
                                                ),
                                            )}
                                        </span>
                                        {membresia?.is_owner !== true && <small>Comprar paquetes es cosa del propietario: pídeselo a él.</small>}
                                    </span>
                                </div>
                            )}
                            {vista?.sample !== undefined && (
                                <div className="rv-cp-seccion">
                                    <h3>Así le llega</h3>
                                    <div className="rv-cp-muestra">
                                        {b.canal === 'email' && <small>{`De: ${membresia?.business_name ?? ''} · avisos@reservaria.app`}</small>}
                                        {vista.sample.subject != null && <strong>{vista.sample.subject}</strong>}
                                        {vista.sample.body !== undefined && <span>{vista.sample.body}</span>}
                                        {vista.sample.unsubscribe_footer !== undefined && <small>{vista.sample.unsubscribe_footer}</small>}
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                    {id !== null && campana?.datos.status !== 'scheduled' && (
                        <Boton tipo="secundario" rojo icono="trash-2" movil={movil} onClick={() => { setBorrar(true); }}>
                            Borrar el borrador
                        </Boton>
                    )}
                </div>
            </PanelLateral>
            <Ventana
                abierta={confirmar}
                titulo={programada ? 'Guardar los cambios' : b.cuando === 'luego' ? `Programar «${b.nombre.trim()}»` : `Enviar «${b.nombre.trim()}» ahora`}
                texto={b.cuando === 'luego' ? `Les llegará el ${b.fecha.split('-').reverse().join('/')} a las ${b.hora} (hora del negocio) por ${canalEnFrase(b.canal)}. A esa hora se vuelve a calcular a quién llega y se comprueba el saldo.` : `Les llega ahora por ${canalEnFrase(b.canal)}. Una vez encargados, los mensajes no se pueden recuperar.`}
                accion={programada ? 'Guardar' : b.cuando === 'luego' ? 'Programar' : 'Enviar'}
                accionIcono={b.cuando === 'luego' ? 'calendar-clock' : 'send'}
                cancelar="Volver"
                ocupado={ocupado}
                ocupadoTexto={b.cuando === 'luego' ? 'Programando…' : 'Enviando…'}
                movil={movil}
                onAccion={enviar}
                onCerrar={() => { setConfirmar(false); }}
            >
                {vista !== null && <LoQueGasta vista={vista} canal={b.canal} ahora={b.cuando === 'ahora'} />}
            </Ventana>
            <Ventana
                abierta={borrar}
                titulo="Borrar el borrador"
                texto="Se borra esta campaña. No se ha enviado nada."
                accion="Borrar"
                accionIcono="trash-2"
                cancelar="Volver"
                ocupado={ocupado}
                ocupadoTexto="Borrando…"
                movil={movil}
                onAccion={() => {
                    if (id === null) {
                        return;
                    }
                    conOcupado(async () => {
                        const leida = await ops.getCampaign(negocio, id);
                        await ops.deleteCampaign(negocio, id, leida.version ?? '');
                        onCerrar('Borrador borrado.');
                    }, () => undefined);
                }}
                onCerrar={() => { setBorrar(false); }}
            />
        </>
    );
}

const TEXTO_CANAL: Readonly<Record<CanalCampana, string>> = {
    whatsapp: 'Con una plantilla de marketing aprobada por Meta. Gasta un envío del bloque por cliente y un mensaje de tu paquete de marketing por cada uno entregado.',
    telegram: 'Texto libre. Necesita el bloque Asistente IA y gasta envíos.',
    email: 'No gasta envíos. Sale con el diseño de tus emails.',
};

/** «Lo que gasta» (12.2 › Envío): destinatarios, envíos del bloque, paquete de marketing y, en coexistencia, «Tardará» (G707). */
function LoQueGasta({ vista: v, canal, ahora }: { vista: VistaPrevia; canal: CanalCampana; ahora: boolean }) {
    return (
        <>
        <dl className="rv-cp-coste">
            <div>
                <dt>Destinatarios</dt>
                <dd>{`${cifra(v.audience_size)} clientes`}</dd>
            </div>
            <div>
                <dt>Fuera sin permiso</dt>
                <dd>{cifra(v.excluded_no_consent)}</dd>
            </div>
            {canal === 'email' ? (
                <div>
                    <dt>Gasta</dt>
                    <dd>Nada: el email no cuenta</dd>
                </div>
            ) : (
                <div>
                    <dt>Envíos del bloque</dt>
                    <dd>{v.sends_remaining == null ? cifra(v.sends_needed) : `${cifra(v.sends_needed)} de ${cifra(v.sends_remaining)}`}</dd>
                </div>
            )}
            {v.marketing_pack != null && (
                <div>
                    <dt>Paquete de marketing</dt>
                    <dd>{`Hasta ${cifra(v.marketing_pack.messages_max)} de ${cifra(v.marketing_pack.balance)}`}</dd>
                </div>
            )}
            {v.estimated_send_minutes != null && ahora && (
                <div>
                    <dt>Tardará</dt>
                    <dd>{`Unos ${cifra(v.estimated_send_minutes)} min`}</dd>
                </div>
            )}
        </dl>
            {v.estimated_send_minutes != null && ahora && <small>{`Tardará unos ${cifra(v.estimated_send_minutes)} minutos en enviarse (5 por segundo: usas también la app). No hace falta esperar.`}</small>}
            <small>{`Calculado ${diaYHora(v.computed_at).toLocaleLowerCase('es')}.`}</small>
        </>
    );
}
